import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../components/ui/Modal';
import { Button } from '../../components/ui/Kit';
import ChannelSelector from '../../components/Leads/ChannelSelector';
import { CHANNEL_LABELS } from '../../lib/leadMeta';
import { api } from '../../lib/api';
import { analyzeLeadsCsv, rowValidation } from '../../lib/csvLeadImport';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiEdit2, FiCheck } = FiIcons;

const GENERIC_PREVIEW_COLUMNS = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'lead_type', label: 'Type' },
  { key: 'lead_source', label: 'Source' },
  { key: 'notes', label: 'Notes' },
];

const AGED_PREVIEW_COLUMNS = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'lead_vendor', label: 'Vendor' },
];

const EDITABLE_FIELDS = new Set(['first_name', 'last_name', 'phone', 'email']);

function leadDisplayName(lead) {
  return [lead?.first_name, lead?.last_name].filter(Boolean).join(' ') || '—';
}

function evaluateRow(lead, isRecruit) {
  const { valid, errors } = rowValidation(lead, isRecruit);
  return { valid, errors };
}

export default function ImportCsvLeadsModal({ isOpen, onClose, isRecruits, onImported }) {
  const fileInputRef = useRef(null);
  const { addToast } = useToast();
  const { user } = useAuth();
  const [parseError, setParseError] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [step, setStep] = useState('upload');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [manualVendor, setManualVendor] = useState('');
  const [markAgedNonExclusive, setMarkAgedNonExclusive] = useState(true);
  const [importChannel, setImportChannel] = useState('LD');
  const [rawCsvText, setRawCsvText] = useState('');
  const [importLabel, setImportLabel] = useState('');
  const [labelError, setLabelError] = useState('');
  const [editRows, setEditRows] = useState([]);
  const [editingRows, setEditingRows] = useState(() => new Set());
  const [reviewRows, setReviewRows] = useState([]);
  const [checkedReview, setCheckedReview] = useState(() => new Set());
  const [dryRunLoading, setDryRunLoading] = useState(false);

  const isRecruitForValidation = useMemo(
    () => (analysis?.vendor === 'generic' ? isRecruits : false),
    [analysis, isRecruits],
  );

  const resetState = () => {
    setParseError('');
    setAnalysis(null);
    setStep('upload');
    setImportResult(null);
    setManualVendor('');
    setMarkAgedNonExclusive(true);
    setImportChannel('LD');
    setRawCsvText('');
    setImportLabel('');
    setLabelError('');
    setEditRows([]);
    setEditingRows(new Set());
    setReviewRows([]);
    setCheckedReview(new Set());
    setDryRunLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (!importing) {
      resetState();
      onClose();
    }
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    setParseError('');
    setAnalysis(null);
    setImportResult(null);
    setStep('upload');
    setReviewRows([]);
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? '');
        setRawCsvText(text);
        const result = analyzeLeadsCsv(text, isRecruits, { markAgedNonExclusive, channel: importChannel });
        if (!result.ok) {
          setParseError(result.error || 'Could not read this CSV file.');
          return;
        }
        setAnalysis(result);
        setStep('mapping');
      } catch (err) {
        setParseError(err.message || 'Failed to parse CSV.');
      }
    };
    reader.onerror = () => setParseError('Could not read the file.');
    reader.readAsText(file, 'UTF-8');
  };

  useEffect(() => {
    if (!rawCsvText) return;
    const result = analyzeLeadsCsv(rawCsvText, isRecruits, { markAgedNonExclusive, channel: importChannel });
    if (result.ok) setAnalysis(result);
  }, [markAgedNonExclusive, rawCsvText, isRecruits, importChannel]);

  useEffect(() => {
    if (!analysis?.rows) {
      setEditRows([]);
      setEditingRows(new Set());
      return;
    }

    setImportResult(null);
    setEditingRows(new Set());

    const isRec = analysis.vendor === 'generic' ? isRecruits : false;
    const baseRows = analysis.rows.map((r) => ({
      lead: { ...r.lead },
      valid: r.valid,
      errors: r.errors,
      rowNumber: r.rowNumber,
    }));
    setEditRows(
      baseRows.map((item) => ({
        ...item,
        ...evaluateRow(item.lead, isRec),
      })),
    );
  }, [analysis, isRecruits]);

  const updateRowField = (rowNumber, field, value) => {
    setEditRows((prev) =>
      prev.map((item) => {
        if (item.rowNumber !== rowNumber) return item;
        const lead = { ...item.lead, [field]: value };
        return { ...item, lead, ...evaluateRow(lead, isRecruitForValidation) };
      }),
    );
  };

  const toggleEditRow = (rowNumber) => {
    setEditingRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  };

  const buildImportPayloads = (items) => {
    const trimmedLabel = importLabel.trim();
    const importBatchId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const importedAt = new Date().toISOString();
    const vendorOverride = analysis?.vendor === 'generic' ? manualVendor.trim() : '';

    return items.map((it) => {
      const lead = { ...it.lead };
      if (vendorOverride) lead.lead_vendor = vendorOverride;
      return {
        ...lead,
        import_label: trimmedLabel,
        import_batch_id: importBatchId,
        imported_at: lead.imported_at || importedAt,
        rowNumber: it.rowNumber,
      };
    });
  };

  const attachAgentMatches = async (leadsToImport) => {
    const agentEmails = [
      ...new Set(
        leadsToImport
          .map((l) => String(l.capturing_agent_email ?? '').trim().toLowerCase())
          .filter(Boolean),
      ),
    ];
    let agentMap = {};
    if (agentEmails.length) {
      try {
        agentMap = await api.users.findIdsByEmails(agentEmails);
      } catch (matchErr) {
        console.error('[import-csv] agent email match failed', matchErr);
      }
    }
    return leadsToImport.map((lead) => {
      const emailKey = String(lead.capturing_agent_email ?? '').trim().toLowerCase();
      const matchedAgentId = emailKey ? agentMap[emailKey] : undefined;
      return matchedAgentId ? { ...lead, _matched_agent_id: matchedAgentId } : lead;
    });
  };

  const handleContinueToReview = async () => {
    const trimmedLabel = importLabel.trim();
    if (!trimmedLabel) {
      setLabelError('Import label is required.');
      return;
    }
    setLabelError('');

    const validItems = editRows.filter((it) => it.valid);
    if (!validItems.length) return;

    setDryRunLoading(true);
    try {
      let payloads = buildImportPayloads(validItems);
      payloads = await attachAgentMatches(payloads);

      const dryRun = await api.leads.bulkCreate(payloads, { duplicateMode: 'merge' });

      const rows = (dryRun.allRows || []).map((row) => {
        const idx = row.rowIndex ?? 0;
        const source = payloads[idx];
        const rowNumber = source?.rowNumber ?? idx + 2;
        return {
          ...row,
          csvRow: row.csvRow || source,
          rowNumber,
          reviewKey: rowNumber,
        };
      });

      setReviewRows(rows);
      setCheckedReview(new Set(rows.map((r) => r.reviewKey)));
      setStep('review');
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Duplicate check failed',
        message: err.message || 'Could not analyze duplicates. Please try again.',
      });
    } finally {
      setDryRunLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    const checked = reviewRows.filter((r) => checkedReview.has(r.reviewKey));
    if (!checked.length) return;

    setImporting(true);
    try {
      const newRows = checked.filter((r) => r.status === 'new');
      const duplicateRows = checked.filter((r) => r.status === 'duplicate');

      const result = await api.leads.bulkCreateConfirmed({
        newRows,
        duplicateRows,
        agencyId: user?.agency_id,
        userId: user?.id,
      });

      setImportResult({
        created: result.created ?? 0,
        updated: result.updated ?? 0,
        activityNotesAdded: result.activityNotesAdded ?? 0,
      });
      setStep('complete');

      if (result.created > 0 || result.updated > 0) {
        addToast({
          type: 'success',
          title: 'Import complete',
          message: `${result.created ?? 0} created, ${result.updated ?? 0} updated`,
        });
        onImported?.();
      }
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Import failed',
        message: err.message || 'Could not save leads. Please try again.',
      });
    } finally {
      setImporting(false);
    }
  };

  const counts = useMemo(() => {
    let ready = 0;
    let invalid = 0;
    for (const it of editRows) {
      if (!it.valid) invalid += 1;
      else ready += 1;
    }
    return { ready, invalid };
  }, [editRows]);

  const reviewSummary = useMemo(() => {
    let newCount = 0;
    let dupCount = 0;
    let skipped = 0;
    for (const r of reviewRows) {
      const checked = checkedReview.has(r.reviewKey);
      if (!checked) {
        skipped += 1;
        continue;
      }
      if (r.status === 'new') newCount += 1;
      else if (r.status === 'duplicate') dupCount += 1;
    }
    return { newCount, dupCount, skipped };
  }, [reviewRows, checkedReview]);

  const displayRows = useMemo(() => {
    const problems = editRows.filter((it) => !it.valid);
    const sample = editRows.filter((it) => it.valid).slice(0, 5);
    const seen = new Set();
    const out = [];
    [...problems, ...sample]
      .sort((a, b) => a.rowNumber - b.rowNumber)
      .forEach((it) => {
        if (seen.has(it.rowNumber)) return;
        seen.add(it.rowNumber);
        out.push(it);
      });
    return out.slice(0, 100);
  }, [editRows]);

  const totalDataRows = analysis?.totalDataRows ?? editRows.length;
  const isAgedLeadStore = analysis?.vendor === 'AgedLeadStore';
  const hasAgedLeads = analysis?.rows?.some((r) => r.lead?.lead_type === 'aged');
  const previewColumns = isAgedLeadStore ? AGED_PREVIEW_COLUMNS : GENERIC_PREVIEW_COLUMNS;

  const toggleReviewChecked = (reviewKey) => {
    setCheckedReview((prev) => {
      const next = new Set(prev);
      if (next.has(reviewKey)) next.delete(reviewKey);
      else next.add(reviewKey);
      return next;
    });
  };

  const modalTitle =
    step === 'review'
      ? 'Review import — duplicates'
      : step === 'complete'
        ? 'Import complete'
        : 'Import leads from CSV';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      className="max-w-4xl max-h-[90vh] overflow-y-auto"
    >
      {step !== 'complete' && (
        <>
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Import channel <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              All rows will be assigned to {CHANNEL_LABELS[importChannel] || importChannel}. Lead types are validated for this channel.
            </p>
            <ChannelSelector value={importChannel} onChange={setImportChannel} hideAllChip disabled={importing || step === 'review'} />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Import Label <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={importLabel}
              onChange={(e) => {
                setImportLabel(e.target.value);
                if (labelError) setLabelError('');
              }}
              placeholder="e.g. Dallas Event May 2026"
              className={`w-full text-sm border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                labelError ? 'border-red-300' : 'border-gray-200'
              }`}
              disabled={importing || step === 'review'}
            />
            {labelError ? (
              <p className="mt-1 text-xs text-red-600">{labelError}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                A label to identify this import batch (used for filtering later).
              </p>
            )}
          </div>
        </>
      )}

      {(step === 'upload' || step === 'mapping') && (
        <>
          <p className="text-sm text-gray-500 mb-4">
            Include a header row with columns such as{' '}
            <span className="font-mono text-xs text-gray-700">
              first_name, last_name, phone, email, lead_type, lead_source, notes
            </span>
            . Names can also use spaces (for example &quot;First Name&quot;). Each row needs a first and last name and at least a phone or email.
          </p>

          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/15"
              onChange={handleFile}
              disabled={importing}
            />
          </div>
        </>
      )}

      {parseError && (
        <div className="mb-4 p-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md">
          {parseError}
        </div>
      )}

      {analysis && step === 'mapping' && (
        <>
          {isAgedLeadStore && (
            <div className="mb-4 p-3 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-md flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">AgedLeadStore format detected</span> — fields mapped automatically.
            </div>
          )}

          <div className="mb-4">
            <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300 text-primary focus:ring-primary"
                checked={markAgedNonExclusive}
                onChange={(e) => setMarkAgedNonExclusive(e.target.checked)}
                disabled={importing}
              />
              <span>
                Mark aged leads as non-exclusive (recommended)
                {hasAgedLeads && (
                  <span className="block text-xs text-gray-500 font-normal mt-0.5">
                    Applies to rows with lead type &quot;aged&quot; in this import.
                  </span>
                )}
              </span>
            </label>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">Lead Vendor</label>
            {isAgedLeadStore ? (
              <div className="text-sm font-medium text-gray-900 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                AgedLeadStore
              </div>
            ) : (
              <input
                type="text"
                value={manualVendor}
                onChange={(e) => setManualVendor(e.target.value)}
                placeholder="Enter vendor name (optional)"
                className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled={importing}
              />
            )}
          </div>

          <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-3">
            <span>
              <span className="font-semibold text-gray-900">{totalDataRows}</span> data rows
            </span>
            <span>
              <span className="font-semibold text-green-700">{counts.ready}</span> ready to import
            </span>
            {counts.invalid > 0 && (
              <span>
                <span className="font-semibold text-red-700">{counts.invalid}</span> skipped (validation)
              </span>
            )}
          </div>

          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Preview {displayRows.length < editRows.length ? `(${displayRows.length} of ${editRows.length} rows)` : `(${editRows.length} rows)`} — edit failed rows inline to fix them
          </p>
          <div className="border border-gray-200 rounded-lg overflow-x-auto mb-6">
            <table className="w-full text-xs text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-2 py-2 text-gray-500 font-medium">#</th>
                  {previewColumns.map((col) => (
                    <th key={col.key} className="px-2 py-2 text-gray-500 font-medium whitespace-nowrap">
                      {col.label}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-gray-500 font-medium">OK</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map(({ lead, valid, errors, rowNumber }) => {
                  const isEditing = editingRows.has(rowNumber);
                  const errorText = errors ? Object.values(errors).filter(Boolean).join(', ') : '';
                  return (
                    <tr key={rowNumber} className="group border-b border-gray-100 last:border-0">
                      <td className="px-2 py-2 text-gray-400">{rowNumber}</td>
                      {previewColumns.map((col) => {
                        const editable = EDITABLE_FIELDS.has(col.key);
                        if (editable && isEditing) {
                          return (
                            <td key={col.key} className="px-2 py-1.5">
                              <input
                                type="text"
                                value={lead[col.key] ?? ''}
                                onChange={(e) => updateRowField(rowNumber, col.key, e.target.value)}
                                disabled={importing}
                                className={`w-full min-w-[90px] text-xs border rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-primary ${
                                  errors?.[col.key] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                }`}
                              />
                            </td>
                          );
                        }
                        return (
                          <td
                            key={col.key}
                            className="px-2 py-2 text-gray-800 max-w-[140px] truncate"
                            title={lead[col.key]}
                          >
                            {lead[col.key] || '—'}
                          </td>
                        );
                      })}
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1.5">
                          {!valid ? (
                            <span className="text-red-600 font-medium" title={errorText || undefined}>No</span>
                          ) : (
                            <span className="text-green-600 font-medium">Yes</span>
                          )}
                          {!valid && (
                            <button
                              type="button"
                              onClick={() => toggleEditRow(rowNumber)}
                              disabled={importing}
                              title={isEditing ? 'Done editing' : 'Edit row'}
                              className={`p-1 rounded text-gray-400 hover:text-primary hover:bg-gray-100 transition ${
                                isEditing ? 'opacity-100 text-primary' : 'opacity-0 group-hover:opacity-100'
                              }`}
                            >
                              <SafeIcon icon={isEditing ? FiCheck : FiEdit2} className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {step === 'review' && (
        <>
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
            <span className="font-semibold text-green-700">{reviewSummary.newCount}</span> new leads to create
            {' · '}
            <span className="font-semibold text-amber-700">{reviewSummary.dupCount}</span> duplicates to update
            {reviewSummary.skipped > 0 && (
              <>
                {' · '}
                <span className="font-semibold text-gray-600">{reviewSummary.skipped}</span> unchecked (skipped)
              </>
            )}
          </div>

          <div className="border border-gray-200 rounded-lg overflow-x-auto mb-6 max-h-[50vh] overflow-y-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-2 py-2 w-8" />
                  <th className="px-2 py-2 text-gray-500 font-medium">#</th>
                  <th className="px-2 py-2 text-gray-500 font-medium">Name</th>
                  <th className="px-2 py-2 text-gray-500 font-medium">Phone</th>
                  <th className="px-2 py-2 text-gray-500 font-medium">Email</th>
                  <th className="px-2 py-2 text-gray-500 font-medium">Status</th>
                  <th className="px-2 py-2 text-gray-500 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {reviewRows.map((row) => {
                  const lead = row.csvRow || {};
                  const isDup = row.status === 'duplicate';
                  const checked = checkedReview.has(row.reviewKey);
                  const matchName =
                    row.existingLeadName ||
                    (row.existingLead ? leadDisplayName(row.existingLead) : null);
                  return (
                    <tr
                      key={row.reviewKey}
                      className={`border-b border-gray-100 last:border-0 ${isDup ? 'bg-amber-50' : 'bg-white'}`}
                    >
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-primary focus:ring-primary"
                          checked={checked}
                          onChange={() => toggleReviewChecked(row.reviewKey)}
                          disabled={importing}
                        />
                      </td>
                      <td className="px-2 py-2 text-gray-400">{row.rowNumber}</td>
                      <td className="px-2 py-2 text-gray-800">{leadDisplayName(lead)}</td>
                      <td className="px-2 py-2 text-gray-800">{lead.phone || '—'}</td>
                      <td className="px-2 py-2 text-gray-800 max-w-[160px] truncate" title={lead.email}>
                        {lead.email || '—'}
                      </td>
                      <td className="px-2 py-2">
                        {isDup ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
                            Duplicate — will merge
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800">
                            New Lead
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-gray-600">
                        {isDup && matchName ? (
                          <span>Matches: {matchName}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {step === 'complete' && importResult && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-800">
            <span className="font-semibold text-green-700">{importResult.created}</span> leads created
            {' · '}
            <span className="font-semibold text-blue-700">{importResult.updated}</span> existing leads updated
            {' · '}
            <span className="font-semibold text-gray-700">{importResult.activityNotesAdded}</span> activity notes added
          </p>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        {step === 'complete' ? (
          <Button
            onClick={() => {
              resetState();
              onClose();
            }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={handleClose} disabled={importing}>
              Cancel
            </Button>
            {step === 'mapping' && (
              <Button
                onClick={handleContinueToReview}
                disabled={importing || dryRunLoading || counts.ready === 0}
                isLoading={dryRunLoading}
              >
                Continue to review
              </Button>
            )}
            {step === 'review' && (
              <>
                <Button variant="outline" onClick={() => setStep('mapping')} disabled={importing}>
                  Back
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  disabled={
                    importing ||
                    reviewSummary.newCount + reviewSummary.dupCount === 0
                  }
                  isLoading={importing}
                >
                  Confirm Import
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
